import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/auth-context';
import { PageHeader } from '@/components/shared/PageHeader';
import { RefreshingIndicator } from '@/components/shared/RefreshingIndicator';
import { StateCard } from '@/components/shared/StateCard';
import { TableSkeleton } from '@/components/shared/TableSkeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from '@/hooks/use-toast';
import {
  fetchTeamMembers,
  inviteTeamMember,
  removeInvitation,
  removeTeamMember,
  resendInvitation,
  updateTeamMemberRole,
  updateTeamMemberStatus,
  type TeamMember,
  type TeamPermissionKey,
  type TeamPermissionMap,
  type TeamRole,
} from '@/lib/team';
import { useProjects, useTeamMembers } from '@/hooks/use-app-queries';
import { formatCurrency } from '@/lib/tickets';
import type { ProjectItem } from '@/lib/projects-clients';
import {
  AlertTriangle,
  Check,
  Info,
  Loader2,
  MoreHorizontal,
  Plus,
  Send,
  Shield,
  User,
  UsersRound,
  X,
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SUPPORT_EMAIL as TEAM_SUPPORT_EMAIL } from '@/lib/support-email';

const TEAM_PLAN_USER_LIMIT = 5;

const roleStyles: Record<TeamRole, string> = {
  ADMIN: 'bg-status-signature-bg text-status-signature',
  PM: 'bg-status-pending-bg text-status-pending',
  FIELD: 'bg-muted text-muted-foreground',
};

const rolePermissionTextKey: Record<TeamRole, string> = {
  ADMIN: 'team.rolePermission.admin',
  PM: 'team.rolePermission.pm',
  FIELD: 'team.rolePermission.field',
};

const teamPermissionKeys: TeamPermissionKey[] = [
  'createClients',
  'createProjects',
  'createTickets',
  'scheduleWork',
  'viewPayments',
  'recordPayments',
  'sendPaymentFollowups',
  'exportBillingReports',
];

const rolePermissionDefaults: Record<TeamRole, TeamPermissionMap> = {
  ADMIN: {
    createClients: true,
    createProjects: true,
    createTickets: true,
    scheduleWork: true,
    viewPayments: true,
    recordPayments: true,
    sendPaymentFollowups: true,
    exportBillingReports: true,
  },
  PM: {
    createClients: true,
    createProjects: true,
    createTickets: true,
    scheduleWork: true,
    viewPayments: false,
    recordPayments: false,
    sendPaymentFollowups: false,
    exportBillingReports: false,
  },
  FIELD: {
    createClients: false,
    createProjects: false,
    createTickets: true,
    scheduleWork: true,
    viewPayments: false,
    recordPayments: false,
    sendPaymentFollowups: false,
    exportBillingReports: false,
  },
};

function defaultPermissionsForRole(role: TeamRole): TeamPermissionMap {
  return { ...rolePermissionDefaults[role] };
}

function normalizePermissionsForRole(
  role: TeamRole,
  permissions?: TeamPermissionMap | null,
): TeamPermissionMap {
  if (role === 'ADMIN') return defaultPermissionsForRole('ADMIN');
  return teamPermissionKeys.reduce((next, key) => {
    next[key] = permissions?.[key] ?? rolePermissionDefaults[role][key];
    return next;
  }, {} as TeamPermissionMap);
}

function defaultEmailNotificationsForRole(role: TeamRole) {
  if (role === 'ADMIN') return true;
  return false;
}

function canConfigureEmailNotificationsForRole(role: TeamRole) {
  return role === 'ADMIN' || role === 'PM';
}

function statusClass(status: TeamMember['status']) {
  if (status === 'ACTIVE') return 'text-success';
  if (status === 'INVITED') return 'text-muted-foreground';
  if (status === 'EXPIRED') return 'text-warning';
  return 'text-muted-foreground';
}

function statusDotClass(status: TeamMember['status']) {
  if (status === 'ACTIVE') return 'bg-success';
  if (status === 'INVITED') return 'bg-muted-foreground';
  if (status === 'EXPIRED') return 'bg-warning';
  return 'bg-muted-foreground';
}

function projectStatusLabel(status?: ProjectItem['status']) {
  switch (status) {
    case 'ON_HOLD':
      return 'On hold';
    case 'COMPLETED':
      return 'Completed';
    case 'CANCELLED':
      return 'Cancelled';
    case 'ARCHIVED':
      return 'Archived';
    case 'ACTIVE':
    default:
      return 'Active';
  }
}

function paymentStatusLabel(status?: ProjectItem['paymentStatus']) {
  switch (status) {
    case 'UNPAID':
      return 'Unpaid';
    case 'PARTIALLY_PAID':
      return 'Partially paid';
    case 'PAID':
      return 'Paid';
    case 'NO_APPROVED_TOTAL':
    default:
      return 'No approved total';
  }
}

function formatProjectScheduleSummary(nextSchedule: ProjectItem['nextSchedule']) {
  if (!nextSchedule) return 'Not scheduled';
  const startAt = new Date(nextSchedule.startAt);
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const isSameDay = (left: Date, right: Date) =>
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate();
  const dayLabel = isSameDay(startAt, today)
    ? 'Today'
    : isSameDay(startAt, tomorrow)
      ? 'Tomorrow'
      : startAt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const timeLabel = startAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const duration = nextSchedule.durationMinutes
    ? nextSchedule.durationMinutes % 60 === 0
      ? `${nextSchedule.durationMinutes / 60}h`
      : `${Math.floor(nextSchedule.durationMinutes / 60)}h ${nextSchedule.durationMinutes % 60}m`
    : null;
  return duration ? `${dayLabel} ${timeLabel} · ${duration}` : `${dayLabel} ${timeLabel}`;
}

function teamMemberName(member: TeamMember | null) {
  return member?.fullName?.trim() || member?.email.split('@')[0] || '';
}

export default function Team() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const hasTeamPlanAccess = user?.currentPlan === 'TEAM';

  const [showInvite, setShowInvite] = useState(false);
  const [teamHelpOpen, setTeamHelpOpen] = useState(false);
  const [invitePanelRendered, setInvitePanelRendered] = useState(false);
  const [invitePanelVisible, setInvitePanelVisible] = useState(false);
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<TeamRole>('FIELD');
  const [invitePermissions, setInvitePermissions] = useState<TeamPermissionMap>(() =>
    defaultPermissionsForRole('FIELD'),
  );
  const [inviteEmailNotificationsEnabled, setInviteEmailNotificationsEnabled] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);

  const [viewTarget, setViewTarget] = useState<TeamMember | null>(null);
  const [assignedProjectsTarget, setAssignedProjectsTarget] = useState<TeamMember | null>(null);
  const [roleTarget, setRoleTarget] = useState<TeamMember | null>(null);
  const [nextRole, setNextRole] = useState<TeamRole>('FIELD');
  const [nextPermissions, setNextPermissions] = useState<TeamPermissionMap>(() =>
    defaultPermissionsForRole('FIELD'),
  );
  const [nextEmailNotificationsEnabled, setNextEmailNotificationsEnabled] = useState(false);
  const [savingRole, setSavingRole] = useState(false);

  const [confirmAction, setConfirmAction] = useState<
    | null
    | { type: 'deactivate' | 'reactivate' | 'remove-user' | 'remove-invite'; target: TeamMember }
  >(null);
  const [runningAction, setRunningAction] = useState(false);

  const isAdmin = user?.role === 'ADMIN';
  const teamQuery = useTeamMembers(Boolean(isAdmin && hasTeamPlanAccess));
  const projectsQuery = useProjects(undefined, Boolean(isAdmin && hasTeamPlanAccess));
  const members = useMemo(() => teamQuery.data ?? [], [teamQuery.data]);
  const assignedProjectsByMember = useMemo(() => {
    const map = new Map<string, ProjectItem[]>();
    const activeAssignedProjects = (projectsQuery.data ?? []).filter(
      (project) =>
        project.assignedMember?.id &&
        project.isActive &&
        !project.archivedAt &&
        project.status !== 'ARCHIVED',
    );
    for (const project of activeAssignedProjects) {
      const memberId = project.assignedMember?.id;
      if (!memberId) continue;
      map.set(memberId, [...(map.get(memberId) ?? []), project]);
    }
    return map;
  }, [projectsQuery.data]);
  const assignedProjectsForTarget = assignedProjectsTarget?.id
    ? assignedProjectsByMember.get(assignedProjectsTarget.id) ?? []
    : [];
  const teamSeatCount = useMemo(
    () =>
      members.filter(
        (member) =>
          member.kind === 'user' || (member.kind === 'invitation' && member.status === 'INVITED'),
      ).length,
    [members],
  );
  const teamSeatLimitReached = teamSeatCount >= TEAM_PLAN_USER_LIMIT;
  const teamSupportMailto = useMemo(() => {
    const subject = encodeURIComponent('Custom Team Package Request');
    const body = encodeURIComponent(
      `Hi WorkScope,\n\nI need a custom team package for more than ${TEAM_PLAN_USER_LIMIT} members.\n\nRequested team size:\nWorkspace name:\nAnything else we should know:\n`,
    );
    return `mailto:${TEAM_SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
  }, []);
  const loading = isAdmin ? teamQuery.isLoading && !teamQuery.data : false;
  const error = teamQuery.error instanceof Error ? teamQuery.error.message : null;
  const isRefreshing = teamQuery.isFetching && Boolean(teamQuery.data);

  const canShowEmpty = useMemo(() => {
    const userRows = members.filter((m) => m.kind === 'user');
    const inviteRows = members.filter((m) => m.kind === 'invitation');
    return userRows.length === 0 && inviteRows.length === 0;
  }, [members]);

  const roleLabelText = (role: TeamRole) => {
    if (role === 'ADMIN') return t('team.roles.admin');
    if (role === 'PM') return t('team.roles.projectManager');
    return t('team.roles.fieldTech');
  };

  const statusLabelText = (status: TeamMember['status']) => {
    if (status === 'ACTIVE') return t('status.active');
    if (status === 'INVITED') return t('status.invited');
    if (status === 'EXPIRED') return t('status.expired');
    return t('status.inactive');
  };

  const relativeTimeText = (value?: string | null) => {
    if (!value) return '—';
    const date = new Date(value);
    const diffMs = Date.now() - date.getTime();
    if (!Number.isFinite(diffMs) || diffMs < 0) return '—';

    const mins = Math.floor(diffMs / (1000 * 60));
    if (mins < 1) return t('time.justNow');
    if (mins < 60) return t('time.minutesAgo', { count: mins });
    const hours = Math.floor(mins / 60);
    if (hours < 24) return t('time.hoursAgo', { count: hours });
    const days = Math.floor(hours / 24);
    if (days < 30) return t('time.daysAgo', { count: days });
    return date.toLocaleDateString();
  };

  useEffect(() => {
    if (showInvite) {
      setInvitePanelVisible(false);
      setInvitePanelRendered(true);
      const timeout = window.setTimeout(() => setInvitePanelVisible(true), 10);
      return () => window.clearTimeout(timeout);
    }

    setInvitePanelVisible(false);
    if (!invitePanelRendered) return;
    const timeout = window.setTimeout(() => setInvitePanelRendered(false), 280);
    return () => window.clearTimeout(timeout);
  }, [showInvite, invitePanelRendered]);

  useEffect(() => {
    if (!teamSeatLimitReached || !showInvite) return;
    setShowInvite(false);
  }, [teamSeatLimitReached, showInvite]);

  const submitInvite = async () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      toast({ title: t('team.validEmailRequired'), variant: 'destructive' });
      return;
    }

    const duplicate = members.some((m) => m.email.toLowerCase() === email && m.status !== 'INACTIVE');
    if (duplicate) {
      toast({ title: t('team.emailAlreadyExists'), variant: 'destructive' });
      return;
    }

    setSendingInvite(true);
    try {
      const invited = await inviteTeamMember({
        fullName: inviteName.trim() || undefined,
        email,
        role: inviteRole,
        permissions: normalizePermissionsForRole(inviteRole, invitePermissions),
        emailNotificationsEnabled: canConfigureEmailNotificationsForRole(inviteRole)
          ? inviteEmailNotificationsEnabled
          : undefined,
      });
      queryClient.setQueryData<TeamMember[]>(['team-members'], (prev) =>
        prev ? [invited, ...prev] : [invited],
      );
      setInviteName('');
      setInviteEmail('');
      setInviteRole('FIELD');
      setInvitePermissions(defaultPermissionsForRole('FIELD'));
      setInviteEmailNotificationsEnabled(defaultEmailNotificationsForRole('FIELD'));
      setShowInvite(false);
      toast({ title: t('team.inviteSentTo', { email }) });
    } catch (err) {
      toast({
        title: t('team.inviteFailed'),
        description:
          err instanceof Error
            ? err.message
            : t('team.seatLimitMessage'),
        variant: 'destructive',
      });
    } finally {
      setSendingInvite(false);
    }
  };

  const handleResend = async (target: TeamMember) => {
    if (target.kind !== 'invitation') return;
    try {
      await resendInvitation(target.id);
      toast({ title: t('team.inviteResentTo', { email: target.email }) });
      await queryClient.invalidateQueries({ queryKey: ['team-members'] });
    } catch (err) {
      toast({
        title: t('team.resendFailed'),
        description: err instanceof Error ? err.message : t('common.tryAgain'),
        variant: 'destructive',
      });
    }
  };

  const handleRoleSave = async () => {
    if (!roleTarget || roleTarget.kind !== 'user') return;
    setSavingRole(true);
    try {
      await updateTeamMemberRole(
        roleTarget.id,
        nextRole,
        normalizePermissionsForRole(nextRole, nextPermissions),
        canConfigureEmailNotificationsForRole(nextRole) ? nextEmailNotificationsEnabled : undefined,
      );
      toast({ title: t('team.roleUpdated') });
      setRoleTarget(null);
      await queryClient.invalidateQueries({ queryKey: ['team-members'] });
    } catch (err) {
      toast({
        title: t('team.roleUpdateFailed'),
        description: err instanceof Error ? err.message : t('common.tryAgain'),
        variant: 'destructive',
      });
    } finally {
      setSavingRole(false);
    }
  };

  const runConfirmAction = async () => {
    if (!confirmAction) return;
    setRunningAction(true);
    try {
      if (confirmAction.type === 'deactivate' && confirmAction.target.kind === 'user') {
        await updateTeamMemberStatus(confirmAction.target.id, false);
        toast({ title: t('team.userDeactivated') });
      }
      if (confirmAction.type === 'reactivate' && confirmAction.target.kind === 'user') {
        await updateTeamMemberStatus(confirmAction.target.id, true);
        toast({ title: t('team.userReactivated') });
      }
      if (confirmAction.type === 'remove-user' && confirmAction.target.kind === 'user') {
        await removeTeamMember(confirmAction.target.id);
        toast({ title: t('team.userRemoved') });
      }
      if (confirmAction.type === 'remove-invite' && confirmAction.target.kind === 'invitation') {
        await removeInvitation(confirmAction.target.id);
        toast({ title: t('team.invitationRemoved') });
      }
      setConfirmAction(null);
      await queryClient.invalidateQueries({ queryKey: ['team-members'] });
    } catch (err) {
      toast({
        title: t('team.actionFailed'),
        description: err instanceof Error ? err.message : t('common.tryAgain'),
        variant: 'destructive',
      });
    } finally {
      setRunningAction(false);
    }
  };

  const renderRolePermissions = (
    role: TeamRole,
    permissions: TeamPermissionMap,
    onPermissionChange: (permission: TeamPermissionKey, checked: boolean) => void,
    emailNotificationsEnabled: boolean,
    onEmailNotificationsChange: (checked: boolean) => void,
  ) => {
    const emailNotificationsConfigurable = canConfigureEmailNotificationsForRole(role);
    const emailNotificationsChecked = emailNotificationsConfigurable && emailNotificationsEnabled;

    return (
      <div className="rounded-md border bg-muted/20 p-3">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-sm font-semibold text-[#0C1F4F]">{t('team.permissions.title')}</p>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              {role === 'ADMIN' ? t('team.permissions.adminFullAccess') : t('team.permissions.helper')}
            </p>
          </div>
        </div>
        {role === 'ADMIN' ? null : (
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {teamPermissionKeys.map((permission) => {
              const checked = permissions[permission];
              return (
                <label
                  key={permission}
                  className={`flex cursor-pointer items-center gap-2 rounded-md border px-2.5 py-2 text-xs transition-colors ${
                    checked
                      ? 'border-success/30 bg-success/5 text-foreground'
                      : 'border-border bg-background/80 text-muted-foreground hover:border-muted-foreground/40'
                  }`}
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-border accent-[#F59E0B]"
                    checked={checked}
                    onChange={(event) => onPermissionChange(permission, event.target.checked)}
                  />
                  <span>{t(`team.permissions.items.${permission}`)}</span>
                </label>
              );
            })}
          </div>
        )}
        <label
          className={`mt-3 flex items-start gap-2 rounded-md border px-2.5 py-2 text-xs transition-colors ${
            emailNotificationsConfigurable
              ? 'cursor-pointer'
              : 'cursor-not-allowed opacity-75'
          } ${
            emailNotificationsChecked
              ? 'border-accent/40 bg-accent/5 text-foreground'
              : 'border-border bg-background/80 text-muted-foreground hover:border-muted-foreground/40'
          }`}
        >
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-border accent-[#F59E0B]"
            checked={emailNotificationsChecked}
            disabled={!emailNotificationsConfigurable}
            onChange={(event) => onEmailNotificationsChange(event.target.checked)}
          />
          <span>
            <span className="block font-medium">{t('team.permissions.emailNotifications')}</span>
            <span className="mt-0.5 block text-[11px] leading-4 text-muted-foreground">
              {emailNotificationsConfigurable
                ? t('team.permissions.emailNotificationsHelp')
                : t('team.permissions.emailNotificationsUnavailable')}
            </span>
          </span>
        </label>
      </div>
    );
  };

  if (!hasTeamPlanAccess) {
    return (
      <div className="space-y-4 animate-fade-in">
        <PageHeader title={t("pages.team.title")} description={t("pages.team.description")} />
        <StateCard
          icon={UsersRound}
          tone="warning"
          title={t("pages.team.planRequired")}
          description={t("pages.team.planRequiredDescription")}
          primaryActionLabel={t("topbar.upgradePlan")}
          onPrimaryAction={() => navigate('/upgrade-plan?targetPlan=team&interval=MONTHLY')}
        />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="space-y-4 animate-fade-in">
        <PageHeader title={t("pages.team.title")} description={t("pages.team.description")} />
        <StateCard
          icon={Shield}
          tone="warning"
          title={t('team.permissionDenied')}
          description={t('team.adminOnly')}
          primaryActionLabel={t('team.goToDashboard')}
          onPrimaryAction={() => navigate('/dashboard')}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <PageHeader
        title={
          <div className="flex items-center gap-2">
            <span>{t("pages.team.title")}</span>
            <Popover open={teamHelpOpen} onOpenChange={setTeamHelpOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-transparent text-muted-foreground transition-colors hover:bg-muted/30 hover:text-[#0C1F4F]"
                  aria-label={t('team.help.ariaLabel')}
                >
                  <Info className="h-4 w-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="w-[390px] border-border/80 bg-white p-4 shadow-lg"
              >
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-semibold text-[#0C1F4F]">{t('team.help.title')}</p>
                    <p className="mt-1 text-sm leading-6 text-muted-foreground">{t('team.help.subtitle')}</p>
                  </div>
                  <ul className="space-y-2 text-sm leading-6 text-muted-foreground">
                    <li>{t('team.help.items.access')}</li>
                    <li>{t('team.help.items.pm')}</li>
                    <li>{t('team.help.items.admin')}</li>
                    <li>{t('team.help.items.scope')}</li>
                  </ul>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        }
        description={t("pages.team.description")}
        actions={
          <div className="flex items-center gap-3">
            <RefreshingIndicator visible={isRefreshing} />
            <Button
              className="bg-accent text-accent-foreground hover:bg-accent/90 gap-1.5 font-semibold"
              size="sm"
              disabled={teamSeatLimitReached}
              onClick={() => setShowInvite(true)}
            >
              <Plus className="h-4 w-4" /> {t('team.inviteUser')}
            </Button>
          </div>
        }
      />

      <div className="rounded-md border bg-card px-4 py-3 text-sm text-muted-foreground">
        {t('team.inviteHelp')}
      </div>

      {teamSeatLimitReached ? (
        <div className="rounded-md border border-warning/30 bg-warning/10 px-4 py-3">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="space-y-1">
              <p className="text-sm font-semibold text-warning">{t('team.seatLimitTitle')}</p>
              <p className="text-sm text-warning">{t('team.seatLimitMessage')}</p>
              <p className="text-xs text-warning/80">
                {t('team.seatUsage', { used: teamSeatCount, max: TEAM_PLAN_USER_LIMIT })}
              </p>
            </div>
            <Button asChild size="sm" className="w-full md:w-auto">
              <a href={teamSupportMailto}>{t('team.contactSupportForCustomPackage')}</a>
            </Button>
          </div>
        </div>
      ) : null}

      {invitePanelRendered && (
        <div
          className={`overflow-hidden rounded-lg border bg-card transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
            invitePanelVisible
              ? 'max-h-[680px] translate-y-0 opacity-100'
              : 'max-h-0 -translate-y-3 opacity-0'
          }`}
        >
          <div className="space-y-4 p-5">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-[#0C1F4F]">{t('team.inviteTeamMember')}</h2>
            <button
              onClick={() => setShowInvite(false)}
              className="p-1 rounded hover:bg-secondary transition-colors"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label className="text-[#0C1F4F]">{t('team.fullName')} ({t('common.optional')})</Label>
              <Input value={inviteName} onChange={(e) => setInviteName(e.target.value)} placeholder={t('team.fullNamePlaceholder')} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#0C1F4F]">{t('login.email')}</Label>
              <Input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} type="email" placeholder={t('team.emailPlaceholder')} />
            </div>
            <div className="mt-[10px] space-y-1.5">
              <div className="flex items-center gap-2">
                <Label className="text-[#0C1F4F]">{t('createAccount.role')}</Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type="button" className="text-xs text-muted-foreground underline">{t('team.roleHelpLink')}</button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t('team.roleHelp')}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <select
                className="w-full h-9 rounded-md border bg-background px-3 text-sm"
                value={inviteRole}
                onChange={(e) => {
                  const role = e.target.value as TeamRole;
                  setInviteRole(role);
                  setInvitePermissions(defaultPermissionsForRole(role));
                  setInviteEmailNotificationsEnabled(defaultEmailNotificationsForRole(role));
                }}
              >
                <option value="FIELD">{roleLabelText('FIELD')}</option>
                <option value="PM">{roleLabelText('PM')}</option>
                <option value="ADMIN">{roleLabelText('ADMIN')}</option>
              </select>
            </div>
          </div>
          {renderRolePermissions(
            inviteRole,
            invitePermissions,
            (permission, checked) =>
              setInvitePermissions((current) => ({ ...current, [permission]: checked })),
            inviteEmailNotificationsEnabled,
            setInviteEmailNotificationsEnabled,
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowInvite(false)}>
              {t('login.cancel')}
            </Button>
            <Button
              className="bg-accent text-accent-foreground hover:bg-accent/90 gap-1.5 font-semibold"
              onClick={() => void submitInvite()}
              disabled={sendingInvite || teamSeatLimitReached}
            >
              {sendingInvite ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} {t('team.sendInvite')}
            </Button>
          </div>
          </div>
        </div>
      )}

      {error ? (
        <StateCard
          icon={AlertTriangle}
          tone="error"
          title={t('team.unableToLoad')}
          description={error}
          primaryActionLabel={t('dashboard.retry')}
          onPrimaryAction={() => void teamQuery.refetch()}
        />
      ) : loading ? (
        <TableSkeleton rows={5} columns={7} />
      ) : canShowEmpty ? (
        <StateCard
          icon={UsersRound}
          title={t('team.noMembers')}
          description={t('team.inviteFirst')}
          primaryActionLabel={t('team.inviteUser')}
          onPrimaryAction={() => setShowInvite(true)}
        />
      ) : (
        <div className="rounded-lg border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="text-left px-4 py-3 font-medium text-primary">{t('team.name')}</th>
                <th className="text-left px-4 py-3 font-medium text-primary hidden sm:table-cell">{t('login.email')}</th>
                <th className="text-left px-4 py-3 font-medium text-primary">{t('createAccount.role')}</th>
                <th className="text-left px-4 py-3 font-medium text-primary hidden md:table-cell">
                  {t('team.assignedProjects', 'Assigned Projects')}
                </th>
                <th className="text-left px-4 py-3 font-medium text-primary">{t('team.status')}</th>
                <th className="text-left px-4 py-3 font-medium text-primary hidden lg:table-cell">{t('team.lastLogin')}</th>
                <th className="px-4 py-3 w-10" />
              </tr>
            </thead>
            <tbody>
              {members.map((m) => {
                const isSelf = m.kind === 'user' && m.id === user?.id;
                const canEditRole = m.kind === 'user' && !isSelf;
                const canToggleStatus = m.kind === 'user' && !isSelf;
                const canRemove = m.kind === 'invitation' || (m.kind === 'user' && !isSelf);
                const assignedProjects = m.kind === 'user' ? assignedProjectsByMember.get(m.id) ?? [] : [];
                return (
                  <tr key={`${m.kind}-${m.id}`} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-7 w-7 rounded-full bg-primary flex items-center justify-center shrink-0">
                          <User className="h-3.5 w-3.5 text-primary-foreground" />
                        </div>
                        <span className="font-medium text-foreground">{m.fullName || m.email.split('@')[0]}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">{m.email}</td>
                    <td className="px-4 py-3">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full cursor-help ${roleStyles[m.role]}`}>
                            {roleLabelText(m.role)}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{t(rolePermissionTextKey[m.role])}</p>
                        </TooltipContent>
                      </Tooltip>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      {m.kind === 'user' && assignedProjects.length > 0 ? (
                        <button
                          type="button"
                          className="text-sm font-semibold text-accent hover:underline"
                          onClick={() => setAssignedProjectsTarget(m)}
                        >
                          {assignedProjects.length} {assignedProjects.length === 1 ? 'project' : 'projects'}
                        </button>
                      ) : (
                        <span className="text-sm text-muted-foreground">
                          {m.kind === 'user' ? 'No projects' : '—'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium flex items-center gap-1 ${statusClass(m.status)}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${statusDotClass(m.status)}`} />
                        {statusLabelText(m.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground hidden lg:table-cell">{relativeTimeText(m.lastLoginAt)}</td>
                    <td className="px-4 py-3">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-1 rounded hover:bg-secondary transition-colors">
                            <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setViewTarget(m)}>{t('team.view')}</DropdownMenuItem>
                          {m.kind === 'user' ? (
                            <DropdownMenuItem onClick={() => setAssignedProjectsTarget(m)}>
                              {t('team.viewAssignedProjects', 'View assigned projects')}
                            </DropdownMenuItem>
                          ) : null}
                          {canEditRole ? (
                            <DropdownMenuItem
                              onClick={() => {
                                setRoleTarget(m);
                                setNextRole(m.role);
                                setNextPermissions(normalizePermissionsForRole(m.role, m.permissions));
                                setNextEmailNotificationsEnabled(m.emailNotificationsEnabled);
                              }}
                            >
                              {t('team.editRole')}
                            </DropdownMenuItem>
                          ) : null}
                          {m.kind === 'invitation' && (m.status === 'INVITED' || m.status === 'EXPIRED') ? (
                            <DropdownMenuItem onClick={() => void handleResend(m)}>{t('team.resendInvite')}</DropdownMenuItem>
                          ) : null}
                          {canToggleStatus && m.kind === 'user' ? (
                            <DropdownMenuItem
                              onClick={() =>
                                setConfirmAction({
                                  type: m.isActive ? 'deactivate' : 'reactivate',
                                  target: m,
                                })
                              }
                            >
                              {m.isActive ? t('team.deactivate') : t('team.reactivate')}
                            </DropdownMenuItem>
                          ) : null}
                          {canRemove ? (
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={() =>
                                setConfirmAction({
                                  type: m.kind === 'invitation' ? 'remove-invite' : 'remove-user',
                                  target: m,
                                })
                              }
                            >
                              {t('team.remove')}
                            </DropdownMenuItem>
                          ) : null}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={Boolean(viewTarget)} onOpenChange={(open) => !open && setViewTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('team.userDetails')}</DialogTitle>
          </DialogHeader>
          {viewTarget ? (
            <div className="space-y-2 text-sm">
              <p><span className="text-muted-foreground">{t('team.name')}:</span> {viewTarget.fullName || '—'}</p>
              <p><span className="text-muted-foreground">{t('login.email')}:</span> {viewTarget.email}</p>
              <p><span className="text-muted-foreground">{t('createAccount.role')}:</span> {roleLabelText(viewTarget.role)}</p>
              <p><span className="text-muted-foreground">{t('team.status')}:</span> {statusLabelText(viewTarget.status)}</p>
              <p><span className="text-muted-foreground">{t('team.lastLogin')}:</span> {relativeTimeText(viewTarget.lastLoginAt)}</p>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(assignedProjectsTarget)}
        onOpenChange={(open) => !open && setAssignedProjectsTarget(null)}
      >
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('team.assignedProjects', 'Assigned projects')}</DialogTitle>
            <DialogDescription>
              {assignedProjectsTarget
                ? `${teamMemberName(assignedProjectsTarget)} · ${assignedProjectsTarget.email}`
                : ''}
            </DialogDescription>
          </DialogHeader>

          {assignedProjectsTarget ? (
            <div className="space-y-3">
              {assignedProjectsForTarget.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border/70 px-4 py-6 text-sm text-muted-foreground">
                  {t(
                    'team.noAssignedProjects',
                    'No projects assigned to this team member yet.',
                  )}
                </div>
              ) : (
                assignedProjectsForTarget.map((project) => (
                  <button
                    key={project.id}
                    type="button"
                    className="w-full rounded-lg border bg-card px-4 py-3 text-left transition-colors hover:bg-muted/35"
                    onClick={() => {
                      setAssignedProjectsTarget(null);
                      navigate(`/projects/${project.id}`);
                    }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-accent">
                          {project.name}
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          Client: {project.client?.name ?? 'No client'}
                        </p>
                      </div>
                      <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                        {projectStatusLabel(project.status)}
                      </span>
                    </div>
                    <div className="mt-3 grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
                      <p>
                        <span className="font-medium text-foreground">Schedule:</span>{' '}
                        {formatProjectScheduleSummary(project.nextSchedule)}
                      </p>
                      <p>
                        <span className="font-medium text-foreground">Balance:</span>{' '}
                        {formatCurrency(project.balance ?? 0)} · {paymentStatusLabel(project.paymentStatus)}
                      </p>
                    </div>
                  </button>
                ))
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(roleTarget)} onOpenChange={(open) => !open && setRoleTarget(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('team.editRole')}</DialogTitle>
            <DialogDescription>{t('team.roleHelp')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>{t('createAccount.role')}</Label>
            <select
              className="w-full h-9 rounded-md border bg-background px-3 text-sm"
              value={nextRole}
              onChange={(e) => {
                const role = e.target.value as TeamRole;
                setNextRole(role);
                setNextPermissions(defaultPermissionsForRole(role));
                setNextEmailNotificationsEnabled(defaultEmailNotificationsForRole(role));
              }}
            >
              <option value="FIELD">{roleLabelText('FIELD')}</option>
              <option value="PM">{roleLabelText('PM')}</option>
              <option value="ADMIN">{roleLabelText('ADMIN')}</option>
            </select>
          </div>
          {renderRolePermissions(
            nextRole,
            nextPermissions,
            (permission, checked) =>
              setNextPermissions((current) => ({ ...current, [permission]: checked })),
            nextEmailNotificationsEnabled,
            setNextEmailNotificationsEnabled,
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleTarget(null)}>{t('login.cancel')}</Button>
            <Button onClick={() => void handleRoleSave()} disabled={savingRole}>
              {savingRole ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {t('team.saveChanges')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(confirmAction)} onOpenChange={(open) => !open && setConfirmAction(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('team.confirmAction')}</DialogTitle>
            <DialogDescription>
              {confirmAction?.type === 'remove-user'
                ? t('team.confirmRemoveUser')
                : confirmAction?.type === 'remove-invite'
                  ? t('team.confirmRemoveInvite')
                  : confirmAction?.type === 'deactivate'
                    ? t('team.confirmDeactivate')
                    : t('team.confirmReactivate')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmAction(null)}>{t('login.cancel')}</Button>
            <Button variant="destructive" onClick={() => void runConfirmAction()} disabled={runningAction}>
              {runningAction ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {t('team.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
